import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthProvider } from "@/hooks/useAuth";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PaymentProviderSwitcher } from "@/components/PaymentProviderSwitcher";
import { MfaGate } from "@/components/MfaGate";

import appCss from "../styles.css?url";

// Sentry bootstrap: all browser-SDK work happens inside useEffect, so the
// Sentry browser SDK is dynamic-imported on the client and never enters SSR.
const SentryBootstrap = lazy(() => import("@/components/SentryBootstrap"));

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <img
          src="/zyntra-logo.png"
          alt="Zyntra Tickets"
          className="mx-auto mb-6 h-20 w-auto opacity-90"
        />
        <h1 className="text-7xl font-bold gradient-text">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscás no existe o fue movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0a0a0f" },
      { title: "Zyntra Tickets — Experiencias que importan" },
      { name: "description", content: "Plataforma premium de venta de entradas a eventos con tickets digitales seguros y experiencia de usuario superior." },
      { property: "og:title", content: "Zyntra Tickets — Experiencias que importan" },
      { property: "og:description", content: "Plataforma premium de venta de entradas a eventos con tickets digitales seguros y experiencia de usuario superior." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Zyntra Tickets" },
      { property: "og:url", content: "https://zyntratickets.com" },
      { property: "og:image", content: "/og-image.png" },
      { property: "og:image:width", content: "1536" },
      { property: "og:image:height", content: "1024" },
      { name: "twitter:title", content: "Zyntra Tickets — Experiencias que importan" },
      { name: "twitter:description", content: "Plataforma premium de venta de entradas a eventos con tickets digitales seguros y experiencia de usuario superior." },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "/og-image.png" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Zyntra Tickets",
          url: "https://zyntratickets.com",
          logo: "https://zyntratickets.com/zyntra-logo.png",
          sameAs: [],
          description:
            "Plataforma premium de venta de entradas a eventos con tickets digitales seguros.",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <AuthProvider>
      <Suspense fallback={null}>
        <SentryBootstrap />
      </Suspense>
      <MfaGate />
      <Header />
      <main className="min-h-screen pt-16">
        <PaymentTestModeBanner />
        <PaymentProviderSwitcher />
        <Outlet />
      </main>
      <Footer />
    </AuthProvider>
  );
}
